import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useBreadcrumbs } from '../../../app/breadcrumb-provider';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import SidebarNav from '../../../components/primitives/SidebarNav';
import { useAuth } from 'app/provider';
import { Message, Conversation, TOOL_STATUS_LABELS, Block } from '../types';
import { fetchConversations, fetchConversationHistory, deleteConversation, renameConversation, sendChatMessageStream } from '../api';
import { SparklesIcon, Bars3Icon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import LoadingState from '../../../components/primitives/LoadingState';

const CUST_STORAGE_KEY = 'selectedCustId';
const ACTIVE_CONV_STORAGE_KEY = 'activeAiConversationId';

const SUGGESTIONS = [
  'How many shipments are in transit right now?',
  'Show my shipments on hold',
  'Top 5 lanes this month',
  'Shipments created per day this month',
  "Export this month's delivered shipments",
  'Show me all shipments expected to arrive today',
];

const AiPage: React.FC = () => {
  const { setBreadcrumbs, setHeaderBorder } = useBreadcrumbs();
  const { userType, customers } = useAuth();

  const [selectedCustId, setSelectedCustId] = useState<string>();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState<boolean>(false);
  const [activeToolStatus, setActiveToolStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUserPrompt, setLastUserPrompt] = useState<string | null>(null);
  const [isSidebarOpenMobile, setIsSidebarOpenMobile] = useState<boolean>(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // References for stream lifecycle, customer cancellation, and race condition avoidance
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeStreamIdRef = useRef<number>(0);
  const activeStreamCustIdRef = useRef<string | null>(null);
  const latestHistoryRequestIdRef = useRef<number>(0);
  const hasLoadedConversationsRef = useRef<boolean>(false);

  useEffect(() => {
    setBreadcrumbs([{ label: 'Ask MRC AI', href: '/ai' }]);
    setHeaderBorder(false);
    return () => setHeaderBorder(true);
  }, [setBreadcrumbs, setHeaderBorder]);

  // Stop any ongoing stream and reset related state
  const stopActiveStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    activeStreamIdRef.current += 1;
    activeStreamCustIdRef.current = null;
    setIsLoading(false);
    setActiveToolStatus(null);
    setStreamingMessageId(null);
  }, []);

  // Abort running stream on unmount
  useEffect(() => {
    return () => {
      stopActiveStream();
    };
  }, [stopActiveStream]);

  // Auto-scroll only when user is already near the bottom (or forced on user send)
  const scrollToBottomIfNear = useCallback((force: boolean = false) => {
    const container = chatContainerRef.current;
    if (!container) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 160;
    if (force || isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottomIfNear(false);
  }, [messages, activeToolStatus, scrollToBottomIfNear]);

  // Initialize selected customer
  useEffect(() => {
    if (!customers?.length) {
      setSelectedCustId(undefined);
      return;
    }

    if (userType === 'Internal') {
      const storedCustId = localStorage.getItem(CUST_STORAGE_KEY);
      const isStoredValid = storedCustId ? customers.some((c) => c.cust_code === storedCustId) : false;
      const defaultCustId: string | undefined = isStoredValid ? storedCustId ?? undefined : customers[0]?.cust_code ?? undefined;

      setSelectedCustId(defaultCustId);
      if (defaultCustId) {
        localStorage.setItem(CUST_STORAGE_KEY, defaultCustId);
      }
      return;
    }
    setSelectedCustId(customers[0]?.cust_code ?? undefined);
  }, [userType, customers]);

  // Fetch conversations for current customer (quiet background updates don't flash skeletons)
  const fetchConversationsList = useCallback(async (custId: string, isBackground: boolean = false) => {
    if (!custId) return;
    try {
      if (!isBackground && !hasLoadedConversationsRef.current) {
        setIsLoadingConversations(true);
      }
      const data = await fetchConversations(custId);
      setConversations(data || []);
      hasLoadedConversationsRef.current = true;
    } catch (err) {
      console.error('Error fetching conversations:', err);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  // Load a conversation's messages and blocks with race condition guard
  const loadConversationHistory = useCallback(
    async (convId: string, custId: string) => {
      if (!convId || !custId) return;
      const reqId = ++latestHistoryRequestIdRef.current;
      try {
        setIsLoadingHistory(true);
        setError(null);
        setStreamingMessageId(null);
        const data = await fetchConversationHistory(convId, custId);

        // Ignore stale responses if user clicked another chat while loading
        if (latestHistoryRequestIdRef.current !== reqId) return;

        setMessages(data.messages);
        setActiveConversationId(convId);
        localStorage.setItem(ACTIVE_CONV_STORAGE_KEY, convId);
      } catch (err: any) {
        if (latestHistoryRequestIdRef.current !== reqId) return;

        if (err?.status === 404) {
          localStorage.removeItem(ACTIVE_CONV_STORAGE_KEY);
          setActiveConversationId(null);
          setStreamingMessageId(null);
          setMessages([]);
          return;
        }
        console.error('Error loading conversation history:', err);
        if (err?.status === 403) {
          setError('You do not have permission to access Ask MRC AI or this customer.');
        } else {
          setError(err.message || 'Could not load conversation');
        }
      } finally {
        if (latestHistoryRequestIdRef.current === reqId) {
          setIsLoadingHistory(false);
        }
      }
    },
    []
  );

  // Load conversations when customer changes
  useEffect(() => {
    if (!selectedCustId) {
      setConversations([]);
      return;
    }

    hasLoadedConversationsRef.current = false;
    fetchConversationsList(selectedCustId, false);

    // Restore active conversation from localStorage if valid
    const storedConvId = localStorage.getItem(ACTIVE_CONV_STORAGE_KEY);
    if (storedConvId) {
      loadConversationHistory(storedConvId, selectedCustId);
    } else {
      setActiveConversationId(null);
      setStreamingMessageId(null);
      setMessages([]);
    }
  }, [selectedCustId, fetchConversationsList, loadConversationHistory]);

  // Handle switching customer (Internal users)
  const handleCustomerChange = (custId: string) => {
    stopActiveStream();
    const nextCustId = custId || undefined;
    setSelectedCustId(nextCustId);
    if (nextCustId) {
      localStorage.setItem(CUST_STORAGE_KEY, nextCustId);
    }
    // Switching customer resets chat and starts fresh
    localStorage.removeItem(ACTIVE_CONV_STORAGE_KEY);
    setActiveConversationId(null);
    setStreamingMessageId(null);
    setMessages([]);
    setError(null);
  };

  // Start a new chat
  const handleNewChat = () => {
    stopActiveStream();
    localStorage.removeItem(ACTIVE_CONV_STORAGE_KEY);
    setActiveConversationId(null);
    setStreamingMessageId(null);
    setMessages([]);
    setError(null);
    setActiveToolStatus(null);
  };

  // Select an existing conversation
  const handleSelectConversation = (convId: string) => {
    if (!selectedCustId || convId === activeConversationId) return;
    stopActiveStream();
    setError(null);
    loadConversationHistory(convId, selectedCustId);
  };

  // Delete a conversation
  const handleDeleteConversation = async (convId: string) => {
    if (!selectedCustId) return;
    try {
      await deleteConversation(convId, selectedCustId);
      setConversations((prev) => prev.filter((c) => c.conversation_id !== convId));
      toast.success('Conversation deleted');

      // If active conversation was deleted, reset chat
      if (activeConversationId === convId) {
        handleNewChat();
      }
    } catch (err: any) {
      toast.error(err.message || 'Error deleting conversation');
    }
  };

  const handleRenameConversation = async (convId: string, title: string) => {
    if (!selectedCustId) return;
    const prevTitle = conversations.find((c) => c.conversation_id === convId)?.title ?? null;
    setConversations((prev) => prev.map((c) => (c.conversation_id === convId ? { ...c, title } : c)));
    try {
      const saved = await renameConversation(convId, selectedCustId, title);
      setConversations((prev) => prev.map((c) => (c.conversation_id === convId ? { ...c, title: saved.title } : c)));
    } catch (err: any) {
      setConversations((prev) => prev.map((c) => (c.conversation_id === convId ? { ...c, title: prevTitle } : c)));
      toast.error(err.message || 'Error renaming conversation');
    }
  };
  // Stop button handler
  const handleStop = useCallback(() => {
    stopActiveStream();
  }, [stopActiveStream]);

  // Handle streaming send
  const handleSendStreaming = async (text: string) => {
    if (!selectedCustId) {
      setError('Customer not selected. Please select a customer first.');
      return;
    }

    // Abort any existing stream and initiate a new one
    stopActiveStream();
    const streamId = ++activeStreamIdRef.current;
    const streamCustId = selectedCustId;
    activeStreamCustIdRef.current = streamCustId;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLastUserPrompt(text);
    const userMsgId = Date.now().toString();
    const userMsg: Message = { id: userMsgId, role: 'user', content: text, blocks: [] };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);
    setActiveToolStatus(null);

    const assistantMsgId = (Date.now() + 1).toString();
    setStreamingMessageId(assistantMsgId);
    setMessages((prev) => [...prev, { id: assistantMsgId, role: 'assistant', content: '', blocks: [] }]);

    // Force scroll to bottom on user send
    setTimeout(() => scrollToBottomIfNear(true), 50);

    try {
      const res = await sendChatMessageStream(
        {
          message: text,
          conversation_id: activeConversationId,
          cust_id: streamCustId,
        },
        abortController.signal
      );

      // Handle specific HTTP error status codes
      if (!res.ok) {
        if (res.status === 429) {
          throw new Error("You're sending messages too fast. Please wait a minute.");
        }
        if (res.status === 403) {
          const errText = await res.text().catch(() => '');
          if (errText && (errText.includes('permission') || errText.includes('menu'))) {
            throw new Error('You do not have permission to access Ask MRC AI.');
          }
          throw new Error('You do not have access to this customer or AI features.');
        }
        if (res.status === 404) {
          localStorage.removeItem(ACTIVE_CONV_STORAGE_KEY);
          setActiveConversationId(null);
          throw new Error('Conversation not found. Starting a new chat...');
        }
        if (res.status === 400 || res.status === 422) {
          throw new Error('Message is empty or over 1,000 characters.');
        }
        const errText = await res.text().catch(() => '');
        throw new Error(`Server error (${res.status}): ${errText || res.statusText}`);
      }

      if (!res.body) throw new Error('No response body received');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';
      const accumulatedBlocks: Block[] = [];
      let loop = true;
      let buffer = '';
      let streamError: string | null = null;
      let receivedDone = false;

      const updateAssistantText = (t: string) => {
        if (activeStreamIdRef.current !== streamId) return;
        setMessages((currentMessages) =>
          currentMessages.map((msg) =>
            msg.id === assistantMsgId ? { ...msg, content: t } : msg
          )
        );
      };

      const updateAssistantBlocks = (b: Block[]) => {
        if (activeStreamIdRef.current !== streamId) return;
        setMessages((currentMessages) =>
          currentMessages.map((msg) =>
            msg.id === assistantMsgId ? { ...msg, blocks: b } : msg
          )
        );
      };

      let streamConvId = activeConversationId;
      while (loop) {
        const { done, value } = await reader.read();
        if (done) {
          loop = false;
          break;
        }

        // If stream was aborted or superseded, exit immediately
        if (activeStreamIdRef.current !== streamId) {
          loop = false;
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          const jsonStr = trimmed.replace('data: ', '').trim();
          if (jsonStr === '[DONE]') {
            receivedDone = true;
            loop = false;
            break;
          }

          let event: any;
          try {
            event = JSON.parse(jsonStr);
          } catch (e) {
            console.error('Error parsing SSE event JSON:', e, jsonStr);
            continue;
          }

          if (activeStreamIdRef.current !== streamId) break;

          // Handle SSE event types
          if (event.type === 'conversation_id') {
            const newConvId = event.conversation_id;
            if (activeStreamCustIdRef.current === streamCustId) {
              streamConvId = newConvId;
              setActiveConversationId(newConvId);
              localStorage.setItem(ACTIVE_CONV_STORAGE_KEY, newConvId);
              // new chat appears in the sidebar as "New chat"
              setConversations((prev) =>
                prev.some((c) => c.conversation_id === newConvId)
                  ? prev
                  : [{ conversation_id: newConvId, title: null, created_at: new Date().toISOString(), updated_at: new Date().toISOString(), message_count: 0 } as Conversation, ...prev]
              );
            }
          } else if (event.type === 'title') {
            const convId = streamConvId;
            if (convId && event.title && activeStreamCustIdRef.current === streamCustId) {
              setConversations((prev) => prev.map((c) => (c.conversation_id === convId ? { ...c, title: event.title } : c)));
            }
          } else if (event.type === 'text') {
            accumulatedContent += event.content;
            updateAssistantText(accumulatedContent);
          } else if (event.type === 'tool_call') {
            const toolName = event.tool_name || '';
            const statusLabel = TOOL_STATUS_LABELS[toolName] || `Executing ${toolName}…`;
            setActiveToolStatus(statusLabel);
          } else if (event.type === 'block') {
            if (event.block) {
              accumulatedBlocks.push(event.block);
              updateAssistantBlocks([...accumulatedBlocks]);
            }
          } else if (event.type === 'done') {
            receivedDone = true;
            setActiveToolStatus(null);
            loop = false;
            break;
          } else if (event.type === 'error') {
            streamError = event.message || 'An error occurred';
            loop = false;
            break;
          }
        }
      }

      // If stream was aborted by user or superseded, finish cleanly without error
      if (abortController.signal.aborted || activeStreamIdRef.current !== streamId) {
        return;
      }

      if (streamError) {
        throw new Error(streamError);
      }

      // Dropped connection check: connection ended without done or error event
      if (!receivedDone) {
        throw new Error('Connection lost before answer finished. Please retry.');
      }

      // Refresh conversations list in background quietly only if customer hasn't changed
      if (selectedCustId === streamCustId) {
        fetchConversationsList(streamCustId, true);
      }
    } catch (err: any) {
      // If error was triggered by abort controller cancellation, ignore silently
      if (err?.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }

      console.error('Chat error:', err);
      const errMsg = err.message || 'Something went wrong. Please try again.';
      setError(errMsg);

      // Clean up empty assistant placeholder if no tokens arrived
      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1];
        if (lastMsg && lastMsg.id === assistantMsgId && !lastMsg.content && (!lastMsg.blocks || lastMsg.blocks.length === 0)) {
          return prev.slice(0, -1);
        }
        return prev;
      });
    } finally {
      if (activeStreamIdRef.current === streamId) {
        setIsLoading(false);
        setActiveToolStatus(null);
        setStreamingMessageId(null);
      }
    }
  };

  const handleRetryLast = () => {
    if (lastUserPrompt) {
      handleSendStreaming(lastUserPrompt);
    }
  };

  return (
    <div className="flex bg-white relative -m-4" style={{ height: 'calc(100% + 2rem)', width: 'calc(100% + 2rem)' }}>
      <SidebarNav
        userType={userType}
        customers={customers}
        selectedCustId={selectedCustId}
        onSelectCustomer={handleCustomerChange}
        conversations={conversations}
        activeConversationId={activeConversationId}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        onDeleteConversation={handleDeleteConversation}
        onRenameConversation={handleRenameConversation}
        isLoadingConversations={isLoadingConversations}
        isOpenMobile={isSidebarOpenMobile}
        onCloseMobile={() => setIsSidebarOpenMobile(false)}
      />

      <div className="flex-1 flex flex-col h-full md:-mt-16 md:h-[calc(100%+4rem)] min-w-0 bg-white relative z-10 overflow-hidden">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between p-2 border-b border-gray-100 bg-gray-50/50">
          <button onClick={() => setIsSidebarOpenMobile(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg shadow-sm">
            <Bars3Icon className="w-4 h-4 text-gray-500" />
            <span>Chats</span>
          </button>
          <span className="text-xs font-semibold text-gray-700">Ask MRC AI</span>
          <div className="w-12" />
        </div>

        {/* Global Error Banner (visible in both empty and populated states) */}
        {error && (
          <div className="px-4 pt-3 pb-1 shrink-0">
            <div className="mx-auto max-w-3xl p-3 bg-rose-50 text-rose-800 text-xs rounded-xl border border-rose-200/80 flex items-center justify-between gap-3 shadow-xs animate-in fade-in duration-200">
              <div className="flex items-center gap-2 min-w-0">
                <ExclamationTriangleIcon className="w-4 h-4 text-rose-500 shrink-0" />
                <span className="font-medium truncate">{error}</span>
              </div>
              {lastUserPrompt && (
                <button
                  type="button"
                  onClick={handleRetryLast}
                  className="shrink-0 px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg text-xs transition-colors shadow-2xs"
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        )}

        {isLoadingHistory ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingState variant="Dots" label="Loading conversation…" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-300">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-500 text-white flex items-center justify-center shadow-lg shadow-blue-500/20 mb-4">
              <SparklesIcon className="w-6 h-6" />
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">How can I help you today?</h1>
            <p className="text-gray-500 text-xs sm:text-sm max-w-md mb-8">Ask about shipment statuses, tracking, delivery estimates, lanes, or generate Excel exports.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-4xl px-2">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  onClick={() => handleSendStreaming(suggestion)}
                  disabled={isLoading}
                  className="p-3.5 text-xs sm:text-sm text-left bg-gray-50 hover:bg-white hover:shadow-md border border-gray-200/80 rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900 group"
                >
                  <span className="group-hover:text-blue-600 transition-colors">{suggestion}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6 scroll-smooth" id="chat-container">
            <div className="w-full max-w-5xl xl:max-w-6xl mx-auto flex flex-col">
              {messages.map((msg) => {
                const isStreamingMessage = msg.id === streamingMessageId;

                return (
                  <ChatMessage
                    key={msg.id}
                    role={msg.role}
                    content={msg.content}
                    blocks={msg.blocks}
                    isStreaming={isStreamingMessage && isLoading}
                    toolStatus={isStreamingMessage && isLoading ? activeToolStatus : null}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}

        <ChatInput onSend={handleSendStreaming} onStop={handleStop} isLoading={isLoading} />
      </div>
    </div>
  );
};

export default AiPage;
